import { useState, useRef, useEffect } from 'react';
import './CountryCodeSelect.css';
import { getAllCountriesWithDialCodes } from '../../utils/countryCodes';

/**
 * Searchable country dial-code dropdown, meant to sit to the left of a phone
 * input. Controlled: the parent owns the selected dial code.
 */
const CountryCodeSelect = ({ value, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFilteredCountries = () => {
    const countries = getAllCountriesWithDialCodes();
    const searchTerm = search.toLowerCase().trim();
    if (!searchTerm) return countries;

    return countries.filter(
      country =>
        country.name.toLowerCase().includes(searchTerm) ||
        country.dialCode.includes(searchTerm) ||
        country.countryCode.toLowerCase().includes(searchTerm)
    );
  };

  const handleSelect = dialCode => {
    onChange(dialCode);
    setOpen(false);
    setSearch('');
  };

  const handleToggle = () => {
    if (disabled) return;

    const nextOpen = !open;
    setOpen(nextOpen);

    if (nextOpen) {
      // Focus the search box once the dropdown has rendered
      setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      setSearch('');
    }
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const filtered = getFilteredCountries();
      if (filtered.length > 0) {
        handleSelect(filtered[0].dialCode);
      }
    }
  };

  const filteredCountries = getFilteredCountries();

  return (
    <div className={`ccs-container ${open ? 'open' : ''}`} ref={containerRef}>
      <button
        type="button"
        className="ccs-display-button"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select country code"
      >
        <span>{value}</span>
        <span className="ccs-arrow">▼</span>
      </button>

      {open && (
        <div className="ccs-dropdown" role="listbox">
          <div className="ccs-search-container">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search countries..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="ccs-search-input"
            />
          </div>
          <div className="ccs-dropdown-content">
            {filteredCountries.length > 0 ? (
              filteredCountries.map(country => (
                <div
                  key={`${country.countryCode}-${country.dialCode}`}
                  className={`ccs-option ${value === country.dialCode ? 'selected' : ''}`}
                  onClick={() => handleSelect(country.dialCode)}
                  role="option"
                  aria-selected={value === country.dialCode}
                >
                  {country.displayText}
                </div>
              ))
            ) : (
              <div className="ccs-no-results">No countries found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelect;
